import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {TradePartners} from "../../models/accounting/InvoiceTradePartner";
import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, map, switchMap} from "rxjs/operators";
import {ajax} from "rxjs/ajax";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError} from "../common/EventNotificationActions";
import {OutgoingInvoiceModule} from "../../models/accounting/AccountBook";
import {InvoiceProjectMeta} from "../../models/accounting/InvoiceProjectMeta";


/**
 * Action types for outgoing invoice templates, including trade partners and project metadata.
 */
export type OutgoingInvoiceTemplateActionTypes = OutgoingInvoiceTradePartnersActionTypes | OutgoingInvoiceProjectMetaActionTypes

/**
 * Action to initiate fetching of trade partners for an outgoing invoice.
 */
export interface FetchOutgoingInvoiceTradePartnersAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICE_TRADE_PARTNERS>  {
    /** the id of the customer to fetch trade partners for */
    customerId?: number
    /** The ID of the project to fetch trade partners for. */
    projectId?: number
    /** The ID of the employer. */
    employerId: number
}
/**
 * Action dispatched upon successful fetching of trade partners.
 */
export interface FetchOutgoingInvoiceTradePartnersSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_SUCCESS> {
    /** The fetched trade partners data. */
    tradePartners: TradePartners
    /** The ID of the project. */
    projectId?: number
    /** the id of the customer to fetch trade partners for */
    customerId?: number
}
/**
 * Action dispatched when fetching trade partners fails.
 */
export interface FetchOutgoingInvoiceTradePartnersErrorAction extends ErrorAction<typeof FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_ERROR> {

}

/**
 * Union type for all trade partner related actions.
 */
export type OutgoingInvoiceTradePartnersActionTypes = FetchOutgoingInvoiceTradePartnersAction
    | FetchOutgoingInvoiceTradePartnersSuccessAction
    | FetchOutgoingInvoiceTradePartnersErrorAction

export const FETCH_OUTGOING_INVOICE_TRADE_PARTNERS = "FETCH_OUTGOING_INVOICE_TRADE_PARTNERS";
export const FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_SUCCESS = "FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_SUCCESS";
export const FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_ERROR = "FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_ERROR";

/**
 * Creates an action to fetch trade partners.
 * @param customerId The customer ID.
 * @param projectId The project ID.
 * @param employerId The employer ID.
 * @returns An action of type FETCH_OUTGOING_INVOICE_TRADE_PARTNERS.
 */
export function fetchTradePartners(
    employerId: number,
    projectId?: number,
    customerId?: number
): FetchOutgoingInvoiceTradePartnersAction {
    return { type: FETCH_OUTGOING_INVOICE_TRADE_PARTNERS, projectId, employerId, customerId };
}
/**
 * Creates an action for a successful trade partners fetch.
 * @param projectId The project ID.
 * @param partners The fetched trade partners.
 * @param customerId The customer ID.
 * @returns An action of type FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_SUCCESS.
 */
export function fetchTradePartnersSuccess(
    partners: TradePartners,
    projectId?: number,
    customerId?: number
): FetchOutgoingInvoiceTradePartnersSuccessAction {
    return { type: FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_SUCCESS, tradePartners: partners, projectId, customerId };
}
/**
 * Creates an action for a failed trade partners fetch.
 * @param error The error occurred during fetch.
 * @returns An action of type FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_ERROR.
 */
export function fetchTradePartnersError( error: any ): FetchOutgoingInvoiceTradePartnersErrorAction {
    return { type: FETCH_OUTGOING_INVOICE_TRADE_PARTNERS_ERROR, error };
}

/**
 * Epic to handle fetching of trade partners.
 * Listens for FETCH_OUTGOING_INVOICE_TRADE_PARTNERS and performs an AJAX request.
 * @param action$ Observable of actions.
 * @param store$ State observable for accessing global state.
 * @returns Observable of actions.
 */
export function fetchTradePartnersEpic(
    action$: Observable< FetchOutgoingInvoiceTradePartnersAction >,
    store$: StateObservable< AppState >
) {

    return action$.pipe(
        ofType( FETCH_OUTGOING_INVOICE_TRADE_PARTNERS ),
        switchMap( action => {
            let tradePartnersPath = APP_CONFIG.baseWeb

            if(( action.customerId ?? 0 ) > 0 )
                tradePartnersPath += `invoices/outgoing/templates/trade-partners/${action.customerId}/${action.employerId}`;

            if(( action.projectId ?? 0 ) > 0 )
                tradePartnersPath += `invoices/outgoing/templates/trade-partners/project/${action.employerId}/${action.projectId}`;


            return ajax(
                APP_CONFIG.getRequestObject( tradePartnersPath )
            ).pipe(
                map(response => {
                    return fetchTradePartnersSuccess(
                        response.response as TradePartners,
                        action.projectId,
                        action.customerId
                    )
                }),
                catchError(error => of(
                    fetchTradePartnersError(error ),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutInvoiceTradePartners")
                ))
            );

        })
    )

}

/**
 * Action to initiate fetching of project metadata for an outgoing invoice.
 */
export interface FetchOutgoingInvoiceProjectMetaAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICE_PROJECT_META>  {
    /** The ID of the project. */
    projectId?: number

    /** the id of the employer to fetch trade partners for */
    employerId?: number
}
/**
 * Action dispatched upon successful fetching of project metadata.
 */
export interface FetchOutgoingInvoiceProjectMetaSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICE_PROJECT_META_SUCCESS> {
    /** The fetched project metadata. */
    projectMeta: InvoiceProjectMeta
    /** The ID of the project. */
    projectId?: number
    /** the id of the employer to fetch trade partners for */
    employerId?: number
}
/**
 * Action dispatched when fetching project metadata fails.
 */
export interface FetchOutgoingInvoiceProjectMetaErrorAction extends ErrorAction<typeof FETCH_OUTGOING_INVOICE_PROJECT_META_ERROR> {

}

/**
 * Union type for all project metadata related actions.
 */
export type OutgoingInvoiceProjectMetaActionTypes = FetchOutgoingInvoiceProjectMetaAction
    | FetchOutgoingInvoiceProjectMetaSuccessAction
    | FetchOutgoingInvoiceProjectMetaErrorAction

export const FETCH_OUTGOING_INVOICE_PROJECT_META = "FETCH_OUTGOING_INVOICE_PROJECT_META";
export const FETCH_OUTGOING_INVOICE_PROJECT_META_SUCCESS = "FETCH_OUTGOING_INVOICE_PROJECT_META_SUCCESS";
export const FETCH_OUTGOING_INVOICE_PROJECT_META_ERROR = "FETCH_OUTGOING_INVOICE_PROJECT_META_ERROR";

/**
 * Creates an action to fetch project metadata.
 * @param projectId The project ID.
 * @param employerId The customer ID.
 * @returns An action of type FETCH_OUTGOING_INVOICE_PROJECT_META.
 */
export function fetchProjectMeta( projectId?: number, employerId?: number ): FetchOutgoingInvoiceProjectMetaAction {
    return { type: FETCH_OUTGOING_INVOICE_PROJECT_META, projectId, employerId };
}
/**
 * Creates an action for a successful project metadata fetch.
 * @param projectId The project ID.
 * @param employerId The customer ID.
 * @param projectMeta The fetched project metadata.
 * @returns An action of type FETCH_OUTGOING_INVOICE_PROJECT_META_SUCCESS.
 */
export function fetchProjectMetaSuccess(
    projectMeta: InvoiceProjectMeta,
    projectId?: number,
    employerId?: number,
): FetchOutgoingInvoiceProjectMetaSuccessAction {
    return { type: FETCH_OUTGOING_INVOICE_PROJECT_META_SUCCESS, projectMeta: projectMeta, projectId, employerId };
}
/**
 * Creates an action for a failed project metadata fetch.
 * @param error The error occurred during fetch.
 * @returns An action of type FETCH_OUTGOING_INVOICE_PROJECT_META_ERROR.
 */
export function fetchProjectMetaError( error: any ): FetchOutgoingInvoiceProjectMetaErrorAction {
    return { type: FETCH_OUTGOING_INVOICE_PROJECT_META_ERROR, error };
}

/**
 * Epic to handle fetching of project metadata.
 * Listens for FETCH_OUTGOING_INVOICE_PROJECT_META and performs an AJAX request.
 * @param action$ Observable of actions.
 * @param store$ State observable for accessing global state.
 * @returns Observable of actions.
 */
export function fetchProjectMetaEpic(
    action$: Observable< FetchOutgoingInvoiceProjectMetaAction >,
    store$: StateObservable< AppState >
) {

    return action$.pipe(
        ofType( FETCH_OUTGOING_INVOICE_PROJECT_META ),
        switchMap( action => {
            let projectMetaPath = APP_CONFIG.baseWeb;

            if( (action.projectId ?? 0 ) > 0 ) {
                projectMetaPath += `invoices/outgoing/templates/project-meta/${action.projectId}`;
            }

            if( (action.employerId ?? 0 ) > 0 ) {
                projectMetaPath += `invoices/outgoing/templates/project-meta/employer/${action.employerId}`;
            }


            return ajax(
                APP_CONFIG.getRequestObject( projectMetaPath )
            ).pipe(
                map(response => {
                    return fetchProjectMetaSuccess(
                        response.response as InvoiceProjectMeta,
                        action.projectId,
                        action.employerId
                    )
                }),
                catchError(error => of(
                    fetchProjectMetaError(error ),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutInvoiceProjectMeta")
                ))
            );

        })
    )

}